import React, { useCallback, useMemo, useRef } from 'react';

import { Button } from '@appcharge/shared-ui';
import { Dayjs } from 'dayjs';
import { formatDateByLocale } from 'utils/formattingUtil';

import { FilterPopover } from 'components/FilterComponents/FilterPopover/FilterPopover';
import { FilterType } from 'components/FilterComponents/FilterPopover/types';

const toSingleSelect =
  (onChange: (value: string) => void) =>
  (items: string[]): void =>
    onChange(items[0] ?? '');

interface Coupon {
  name: string;
  promoCodeNames?: string[];
  discountPercentage?: number;
}

interface CouponsFilterBarProps {
  couponNameValue: string;
  onCouponNameChange: (value: string) => void;
  promoCodeValue: string;
  onPromoCodeChange: (value: string) => void;
  discountValue: string;
  onDiscountChange: (value: string) => void;
  statusValues: string[];
  onStatusChange: (values: string[]) => void;
  rangeDates: [Dayjs, Dayjs] | undefined;
  onDateChange: (startDate: string, endDate: string) => void;
  clearFiltersButton: boolean;
  handleClearFilters: () => void;
  couponsData?: Coupon[];
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' }
];

const CouponsFilterBarComponent: React.FC<CouponsFilterBarProps> = ({
  couponNameValue,
  onCouponNameChange,
  promoCodeValue,
  onPromoCodeChange,
  discountValue,
  onDiscountChange,
  statusValues,
  onStatusChange,
  rangeDates,
  onDateChange,
  clearFiltersButton,
  handleClearFilters,
  couponsData
}) => {
  const promoCodeOptions = useMemo(() => {
    if (!couponsData) return [];
    const allPromoCodes = couponsData.flatMap(
      (coupon) => coupon.promoCodeNames || []
    );
    const uniquePromoCodes = Array.from(new Set(allPromoCodes));
    return uniquePromoCodes.map((code) => ({ label: code, value: code }));
  }, [couponsData]);

  const couponNameOptions = useMemo(() => {
    if (!couponsData) return [];
    const uniqueNames = Array.from(
      new Set(couponsData.map((coupon) => coupon.name))
    );
    return uniqueNames.map((name) => ({ label: name, value: name }));
  }, [couponsData]);

  const discountOptions = useMemo(() => {
    if (!couponsData) return [];
    const discounts = couponsData
      .map((coupon) => coupon.discountPercentage)
      .filter((d): d is number => d !== undefined && d !== null);
    const uniqueDiscounts = Array.from(new Set(discounts)).sort(
      (a, b) => a - b
    );
    return uniqueDiscounts.map((d) => ({ label: `${d}%`, value: String(d) }));
  }, [couponsData]);

  const currentDateValue = useMemo(() => {
    if (rangeDates && rangeDates[0] && rangeDates[1]) {
      return JSON.stringify({
        startDate: rangeDates[0].toISOString(),
        endDate: rangeDates[1].toISOString()
      });
    }
    return '';
  }, [rangeDates]);

  const { displayLabel, displayParts } = useMemo(() => {
    if (rangeDates && rangeDates[0] && rangeDates[1]) {
      const prefix = 'Date';
      const start = rangeDates[0];
      const end = rangeDates[1];

      if (start.isSame(end, 'day')) {
        const dateStr = formatDateByLocale(start.toISOString());
        return {
          displayLabel: `${prefix} | ${dateStr}`,
          displayParts: [prefix, dateStr]
        };
      }
      const dateRange = `${formatDateByLocale(start.toISOString())} - ${formatDateByLocale(end.toISOString())}`;
      return {
        displayLabel: `${prefix} | ${dateRange}`,
        displayParts: [prefix, dateRange]
      };
    }
    return {
      displayLabel: 'Created Date',
      displayParts: undefined
    };
  }, [rangeDates]);

  const onDateChangeRef = useRef(onDateChange);
  onDateChangeRef.current = onDateChange;

  const handleDateChange = useCallback((value: string) => {
    if (!value) {
      onDateChangeRef.current('', '');
      return;
    }

    try {
      const parsed = JSON.parse(value);
      if (parsed.startDate && parsed.endDate) {
        onDateChangeRef.current(parsed.startDate, parsed.endDate);
      }
    } catch (error) {
      console.error('Failed to parse date range:', error);
    }
  }, []);

  return (
    <div className="flex flex-row gap-1 items-center flex-wrap">
      <FilterPopover
        label="Promo Code Name"
        filterType={FilterType.AUTOCOMPLETE_CHECKLIST}
        options={promoCodeOptions}
        selectedItems={promoCodeValue ? [promoCodeValue] : []}
        onSelectionChange={toSingleSelect(onPromoCodeChange)}
        placeholder="Enter promo code"
        singleSelect
        dataTestId="couponsPromoCodeFilter"
      />
      <FilterPopover
        label="Coupon Name"
        filterType={FilterType.AUTOCOMPLETE_CHECKLIST}
        options={couponNameOptions}
        selectedItems={couponNameValue ? [couponNameValue] : []}
        onSelectionChange={toSingleSelect(onCouponNameChange)}
        placeholder="Enter coupon name"
        singleSelect
        dataTestId="couponsCouponNameFilter"
      />
      <FilterPopover
        label="Status"
        filterType={FilterType.AUTOCOMPLETE_CHECKLIST}
        options={STATUS_OPTIONS}
        selectedItems={statusValues}
        onSelectionChange={onStatusChange}
        dataTestId="couponsStatusFilter"
      />
      <FilterPopover
        label={displayLabel}
        labelParts={displayParts}
        onChange={handleDateChange}
        selectedItems={currentDateValue ? [currentDateValue] : []}
        filterType={FilterType.DATE_PICKER}
        hideIndicator
        dataTestId="couponsDateFilter"
      />
      <FilterPopover
        label="Discounts"
        filterType={FilterType.AUTOCOMPLETE_CHECKLIST}
        options={discountOptions}
        selectedItems={discountValue ? [discountValue] : []}
        onSelectionChange={toSingleSelect(onDiscountChange)}
        singleSelect
        dataTestId="couponsDiscountFilter"
      />
      {clearFiltersButton && (
        <Button
          variant="link"
          onClick={handleClearFilters}
          className="text-indigo-600 hover:text-base-primary text-xs border-none shadow-none"
        >
          Clear filters
        </Button>
      )}
    </div>
  );
};

export const CouponsFilterBar = React.memo(CouponsFilterBarComponent);

CouponsFilterBar.displayName = 'CouponsFilterBar';
